import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, addDays, startOfDay } from 'date-fns';
import { PiGearSixDuotone } from 'react-icons/pi';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Cell, ReferenceLine, Line } from 'recharts';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupDateRangeQuickSelector, PopupToolbar, PopupToolbarGroup, PopupToolbarChip, PopupBody } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { OptimizationPipelineChartFrame, OptimizationPipelineChartTypeToggle, OptimizationPipelineChartViewToggle, OptimizationPipelineChartEmptyState, buildDateBounds, buildDisabledQuickOptions, formatPercent, toMs, getChartPortalTooltipProps } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineChart';

const POPUP_TYPE = 'UNIT_OPTIMIZER_HISTORY';
const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const CHART_HEIGHT = 440;

const POS_COLOR = '#2E7D32';
const NEG_COLOR = '#C62828';
const WARN_COLOR = '#FBBF24';
const ESTIMATED_COLOR = '#9CA3AF';
const ESTIMATED_BG_COLOR = '#E5E7EB';
const OUTLIER_BG_COLOR = '#FEE2E2';
const SKIPPED_BG_COLOR = '#FEF3C7';
const CUM_LINE_COLOR = '#2E7D32';

function d0(date) { return startOfDay(new Date(date)); }
function keyOf(date) { return format(d0(date), 'yyyy-MM-dd'); }
function formatInputDate(date) { if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''; return format(date, 'yyyy-MM-dd'); }
function parseInputDate(value) { if (!value) return null; const date = new Date(value + 'T00:00:00'); if (Number.isNaN(date.getTime())) return null; return date; }
function formatDisplayDate(value) { const date = value instanceof Date ? value : parseInputDate(value); return date ? format(date, 'yyyy.MM.dd.') : ''; }
function normalizeNumber(value) { if (typeof value === 'number' && Number.isFinite(value)) return value; if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value); return null; }
function formatAxisTick(value, suffix) { return formatLabelValue(value, { thousandsSeparator: false, decimalPlaces: 0 }) + (suffix || ''); }
function computePercentGain(base, value) { if (base == null || value == null || base === 0) return null; return (value / base) * 100; }
function isEstimatedRow(row) { return !!(row && row.estimated); }
function getRowFeatures(row) { return row?.model_validation_data?.features || row?.features || null; }

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);
    if (from && to) return 'Showing data from ' + from + ' to ' + to;
    if (from) return 'Showing data from ' + from;
    if (to) return 'Showing data until ' + to;
    return 'Detailed unit optimizer history';
}

function getModelValidationAccurate(entry) {
    if (typeof entry?.accurate === 'boolean') return entry.accurate;
    if (typeof entry?.model_validation_is_accurate === 'boolean') return entry.model_validation_is_accurate;
    if (typeof entry?.model_validation_data?.accurate === 'boolean') return entry.model_validation_data.accurate;
    if (typeof entry?.meta?.model_validation?.accurate === 'boolean') return entry.meta.model_validation.accurate;

    const features = getRowFeatures(entry);
    const featureAccuracies = Object.values(features || {}).map((feature) => feature?.accurate).filter((value) => typeof value === 'boolean');

    if (featureAccuracies.includes(false)) return false;
    if (featureAccuracies.length && featureAccuracies.every(Boolean)) return true;

    return undefined;
}

function computeDomain(values, emptyMin, emptyMax) {
    if (!values.length) return [emptyMin, emptyMax];

    const minRaw = Math.min(...values);
    const maxRaw = Math.max(...values);

    if (minRaw === maxRaw) {
        if (minRaw === 0) return [emptyMin, emptyMax];

        return minRaw > 0 ? [0, minRaw] : [minRaw, 0];
    }

    return [minRaw, maxRaw];
}

function getRowStatus(row) {
    if (!row?.hasModelValidation) return 'ok';
    if (row.modelAccurate === true) return 'ok';
    if (row.modelAccurate === false) return 'warning';
    return 'ok';
}

function toBarData(rows) {
    if (!Array.isArray(rows)) return [];

    return rows.map((row) => {
        const ts = row?.ts;
        if (!ts) return null;

        const base = normalizeNumber(row?.base_objective);
        const best = normalizeNumber(row?.best_objective);
        if (base == null) return null;

        const meta = row?.meta || {};
        const outlier = !!meta?.outlier_detection?.outlier;
        const value = base != null && best != null ? best - base : null;

        return { ts, base, best, value, isOutlier: outlier, hasModelValidation: false, modelAccurate: null, isSuccessful: !!row?.is_successful, hasBest: best !== null };
    }).filter(Boolean);
}

function renderEstimatedBackground(props) {
    const { x, y, width, height, payload } = props;
    if (!payload) return null;

    let fill = null;
    if (payload.isSkippedDay) fill = SKIPPED_BG_COLOR;
    else if (payload.isOutlierDay) fill = OUTLIER_BG_COLOR;
    else if (payload.status === 'gap') fill = ESTIMATED_BG_COLOR;

    if (!fill) return null;

    return <rect x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.4} />;
}

export default function UnitOptimizerHistoryPopup() {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource } = useOptimizationPipeline();

    const chartRef = useRef(null);

    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('bar');
    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [useScaled, setUseScaled] = useState(false);

    const open = popup?.type === POPUP_TYPE;

    const unitOptimizerResource = getOnDemandResource('unitOptimizerData');
    const unitOptimizerLogResource = getOnDemandResource('unitOptimizerLogDataList');
    const mergedMeasurementResource = getOnDemandResource('mergedMeasurementDataList');
    const modelValidationLogResource = getOnDemandResource('modelValidationLogDataList');

    const unitOptimizerData = unitOptimizerResource?.data;
    const unitOptimizerLogDataList = unitOptimizerLogResource?.data;
    const mergedMeasurementDataList = mergedMeasurementResource?.data;
    const modelValidationLogDataList = modelValidationLogResource?.data;

    const loading = unitOptimizerResource?.loading || unitOptimizerLogResource?.loading || mergedMeasurementResource?.loading || modelValidationLogResource?.loading;
    const error = unitOptimizerResource?.error || unitOptimizerLogResource?.error || mergedMeasurementResource?.error || modelValidationLogResource?.error;

    const title = popup?.title || 'Unit Optimizer History';

    useEffect(() => {
        if (!open) return;

        ensureOnDemandResource('unitOptimizerData');
        ensureOnDemandResource('unitOptimizerLogDataList');
        ensureOnDemandResource('mergedMeasurementDataList');
        ensureOnDemandResource('modelValidationLogDataList');
    }, [open, ensureOnDemandResource]);

    const rawRows = useMemo(() => toBarData(unitOptimizerLogDataList), [unitOptimizerLogDataList]);

    const sortedRows = useMemo(() => {
        return Array.isArray(rawRows) ? [...rawRows].filter((row) => row && row.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts)) : [];
    }, [rawRows]);

    const dateBounds = useMemo(() => buildDateBounds(sortedRows), [sortedRows]);
    const disabledQuickOptions = useMemo(() => buildDisabledQuickOptions(dateBounds.minDate, dateBounds.maxDate), [dateBounds]);

    useEffect(() => {
        if (!open) return;

        setIsChartView(true);
        setChartType('bar');

        if (dateBounds.minDate && dateBounds.maxDate) setDateRange({ from: formatInputDate(dateBounds.minDate), to: formatInputDate(dateBounds.maxDate), quickRange: 'ALL' });
        else setDateRange(INITIAL_DATE_RANGE);
    }, [open, popup?.requestToken, dateBounds.minDate, dateBounds.maxDate]);

    const multiplier = useMemo(() => {
        const value = Number(unitOptimizerData?.multiplier);
        return Number.isFinite(value) ? value : 1;
    }, [unitOptimizerData?.multiplier]);

    const hasScale = useMemo(() => {
        return typeof unitOptimizerData?.uom === 'string' && unitOptimizerData.uom.trim().length > 0 && Number.isFinite(Number(unitOptimizerData?.multiplier));
    }, [unitOptimizerData?.uom, unitOptimizerData?.multiplier]);

    const uomSuffix = useMemo(() => {
        const value = typeof unitOptimizerData?.uom === 'string' ? unitOptimizerData.uom.trim() : '';
        return value ? '\u00A0' + value : '';
    }, [unitOptimizerData?.uom]);

    const cumulativeUomSuffix = useMemo(() => {
        const value = typeof unitOptimizerData?.cumulativeUom === 'string' ? unitOptimizerData.cumulativeUom.trim() : '';
        return value ? '\u00A0' + value : uomSuffix;
    }, [unitOptimizerData?.cumulativeUom, uomSuffix]);

    useEffect(() => {
        setUseScaled(hasScale);
    }, [hasScale]);

    const suffix = hasScale && useScaled ? uomSuffix : '';
    const cumSuffix = hasScale && useScaled ? cumulativeUomSuffix : '';

    const modelAccurateByDay = useMemo(() => {
        const map = new Map();

        if (!Array.isArray(modelValidationLogDataList)) return map;

        modelValidationLogDataList.forEach((entry) => {
            const ts = entry?.ts;
            if (!ts) return;

            const accurate = getModelValidationAccurate(entry);

            if (typeof accurate === 'boolean') {
                map.set(keyOf(ts), accurate);
            }
        });

        return map;
    }, [modelValidationLogDataList]);

    const outlierDays = useMemo(() => {
        const set = new Set();

        if (Array.isArray(mergedMeasurementDataList)) {
            mergedMeasurementDataList.forEach((entry) => {
                const detection = entry?.meta?.outlier_detection;
                const isOutlier = typeof detection?.outlier === 'boolean' ? detection.outlier : false;
                if (isOutlier && entry?.ts) set.add(keyOf(entry.ts));
            });
        }

        return set;
    }, [mergedMeasurementDataList]);

    const skippedDays = useMemo(() => {
        const set = new Set();

        if (Array.isArray(mergedMeasurementDataList)) {
            mergedMeasurementDataList.forEach((entry) => {
                const skipped = entry?.meta?.outlier_detection?.skipped_because_of;
                if (Array.isArray(skipped) && skipped.length && entry?.ts) set.add(keyOf(entry.ts));
            });
        }

        return set;
    }, [mergedMeasurementDataList]);

    const dailyActual = useMemo(() => {
        const map = new Map();

        sortedRows.forEach((row) => {
            if (row.isOutlier) return;
            if (!row.hasBest || row.best == null) return;

            const key = keyOf(row.ts);
            const base = row.base;
            const best = row.best;

            if (base == null || best == null) return;

            const dayAccurate = modelAccurateByDay.has(key) ? modelAccurateByDay.get(key) : undefined;
            const effectiveRow = typeof dayAccurate === 'boolean' ? { ...row, hasModelValidation: true, modelAccurate: dayAccurate } : row;
            const status = getRowStatus(effectiveRow);
            const prev = map.get(key);

            if (prev) {
                map.set(key, { sumBase: prev.sumBase + base, sumBest: prev.sumBest + best, cnt: prev.cnt + 1, status: prev.status === 'warning' || status === 'warning' ? 'warning' : 'ok', modelAccurate: typeof dayAccurate === 'boolean' ? dayAccurate : prev.modelAccurate });
            } else {
                map.set(key, { sumBase: base, sumBest: best, cnt: 1, status, modelAccurate: typeof dayAccurate === 'boolean' ? dayAccurate : undefined });
            }
        });

        const out = new Map();

        map.forEach(({ sumBase, sumBest, cnt, status, modelAccurate }, key) => {
            const base = sumBase / cnt;
            const best = sumBest / cnt;
            out.set(key, { ts: key, base, best, value: best - base, estimated: false, status, modelAccurate });
        });

        return out;
    }, [sortedRows, modelAccurateByDay]);

    const filledDaily = useMemo(() => {
        const fromDate = parseInputDate(dateRange?.from);
        const toDate = parseInputDate(dateRange?.to);

        if (!fromDate || !toDate) return [];

        const from = d0(fromDate);
        const to = d0(toDate);

        if (from > to) return [];

        let cur = from;
        let cumBase = 0;
        let cumBest = 0;
        let cumCnt = 0;
        let lastActualDate = null;

        for (const key of dailyActual.keys()) {
            const date = d0(key);
            if (!lastActualDate || date > lastActualDate) lastActualDate = date;
        }

        const rows = [];

        while (cur <= to) {
            const key = keyOf(cur);
            const actual = dailyActual.get(key);
            const isOutlierDay = outlierDays.has(key);
            const isSkippedDay = skippedDays.has(key);
            const modelAccurate = modelAccurateByDay.has(key) ? modelAccurateByDay.get(key) : undefined;

            if (actual) {
                if (!Number.isNaN(actual.base) && !Number.isNaN(actual.best)) {
                    cumBase += actual.base;
                    cumBest += actual.best;
                    cumCnt += 1;
                }

                rows.push({ ts: cur.toISOString(), base: actual.base, best: actual.best, value: actual.value, estimated: false, status: actual.status, isOutlierDay, isSkippedDay, modelAccurate: typeof actual.modelAccurate === 'boolean' ? actual.modelAccurate : modelAccurate });
            } else {
                const isFuture = lastActualDate && cur > lastActualDate;

                if (isFuture) {
                    const baseAvg = cumCnt > 0 ? cumBase / cumCnt : null;
                    const bestAvg = cumCnt > 0 ? cumBest / cumCnt : null;

                    rows.push({ ts: cur.toISOString(), base: baseAvg, best: bestAvg, value: baseAvg != null && bestAvg != null ? bestAvg - baseAvg : null, estimated: baseAvg != null && bestAvg != null, status: 'estimated', isOutlierDay, isSkippedDay, modelAccurate });
                } else {
                    let status = 'gap';
                    if (isSkippedDay) status = 'skipped';
                    else if (isOutlierDay) status = 'outlier';

                    rows.push({ ts: cur.toISOString(), base: null, best: null, value: null, estimated: false, status, isOutlierDay, isSkippedDay, modelAccurate });
                }
            }

            cur = addDays(cur, 1);
        }

        return rows.some((row) => row.value != null) ? rows : [];
    }, [dailyActual, dateRange, outlierDays, skippedDays, modelAccurateByDay]);

    const displayDaily = useMemo(() => {
        if (!filledDaily.length) return [];
        if (!(hasScale && useScaled) || multiplier === 1) return filledDaily;

        return filledDaily.map((row) => ({
            ...row,
            base: row.base == null ? null : row.base * multiplier,
            best: row.best == null ? null : row.best * multiplier,
            value: row.value == null ? null : row.value * multiplier
        }));
    }, [filledDaily, hasScale, useScaled, multiplier]);

    const chartData = useMemo(() => {
        let cumulative = 0;

        return displayDaily.map((row) => {
            const value = row.value != null && Number.isFinite(row.value) ? row.value : 0;
            cumulative += value;

            return { ...row, cumValue: cumulative };
        });
    }, [displayDaily]);

    const yDomain = useMemo(() => computeDomain(displayDaily.map((row) => row.value).filter((value) => value != null && Number.isFinite(value)), -1, 1), [displayDaily]);
    const cumDomain = useMemo(() => computeDomain(chartData.map((row) => row.cumValue).filter((value) => value != null && Number.isFinite(value)), 0, 1), [chartData]);

    function getTooltip({ active, payload }) {
        if (!(active && payload?.length)) return null;

        const row = payload[0]?.payload;
        if (!row) return null;

        const percent = computePercentGain(row.base, row.value);
        const cumulative = row.cumValue != null ? row.cumValue : null;

        return (
            <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[200px] max-w-[300px]'>
                <div className='mb-2 font-semibold text-[11px] opacity-80'>{format(new Date(row.ts), 'yyyy.MM.dd.')}</div>
                <div className='font-medium'>Base {row.base == null ? '-' : formatLabelValue(row.base, defaultLabelStyle()) + suffix}</div>
                <div className='font-medium'>Best {row.best == null ? '-' : formatLabelValue(row.best, defaultLabelStyle()) + suffix}</div>
                <div className='font-medium'>Gain {row.value == null ? '-' : formatLabelValue(row.value, defaultLabelStyle()) + suffix}{row.estimated ? ' (Estimated)' : ''}</div>
                <div className='font-medium'>Gain % {formatPercent(percent)}</div>
                {cumulative != null && <div className='font-medium'>Cumulative gain {formatLabelValue(cumulative, defaultLabelStyle()) + cumSuffix}</div>}
                {row.status === 'warning' && <div className='font-medium text-yellow-300'>Model validation: invalid</div>}
            </div>
        );
    }

    const tooltipProps = getChartPortalTooltipProps(getTooltip, chartRef);

    const tableConfig = useMemo(() => ({
        columns: [
            { key: 'ts', label: 'Timestamp', sortable: true, width: 140, minWidth: 140, align: 'left', renderCell: ({ row }) => format(new Date(row.ts), 'yyyy.MM.dd.') },
            { key: 'base', label: 'Base', sortable: true, minWidth: 140, align: 'right', renderCell: ({ row }) => row.base == null ? '-' : formatLabelValue(row.base, defaultLabelStyle()) + suffix },
            { key: 'best', label: 'Best', sortable: true, minWidth: 140, align: 'right', renderCell: ({ row }) => row.best == null ? '-' : formatLabelValue(row.best, defaultLabelStyle()) + suffix },
            { key: 'value', label: 'Gain', sortable: true, minWidth: 140, align: 'right', renderCell: ({ row }) => row.value == null ? '-' : formatLabelValue(row.value, defaultLabelStyle()) + suffix },
            { key: 'gainPercent', label: 'Gain %', sortable: true, minWidth: 120, align: 'right', getSortValue: (row) => computePercentGain(row.base, row.value), renderCell: ({ row }) => formatPercent(computePercentGain(row.base, row.value)) },
            { key: 'estimated', label: 'Estimated', sortable: true, minWidth: 110, align: 'center', renderCell: ({ row }) => isEstimatedRow(row) ? 'Yes' : 'No' }
        ],
        sort: { key: 'ts', direction: 'desc' },
        states: { loadingTitle: 'Loading optimizer history...', errorTitle: 'Failed to load optimizer history', emptyTitle: 'No data available', noResultsTitle: 'No matching rows' },
        tableMinWidth: 850
    }), [suffix]);

    const hasDisplayData = chartData.length > 0;

    return (
        <OptimizationPipelinePopup open={open} onClose={closePopup} width='1440px' minWidth={480} maxWidth={1440} overflowVisible>
            <OptimizationPipelinePopupHeader title={title} subtitle={getDateRangeSubtitle(dateRange)} icon={PiGearSixDuotone} onClose={closePopup}>
                <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
            </OptimizationPipelinePopupHeader>

            <PopupToolbar>
                <PopupToolbarGroup label='Display:'>
                    {hasScale && <PopupToolbarChip label={useScaled ? 'Raw' : 'UOM'} active={useScaled} onClick={() => { setUseScaled((prev) => !prev); }} />}
                    {!hasScale && <span className='text-11 text-gray-400'>Raw values</span>}
                </PopupToolbarGroup>

                <PopupToolbarGroup>
                    <OptimizationPipelineChartTypeToggle value={chartType} onChange={setChartType} disabled={!isChartView} />
                    <OptimizationPipelineChartViewToggle isChartView={isChartView} onChange={setIsChartView} />
                </PopupToolbarGroup>
            </PopupToolbar>

            <PopupBody padding={false} scroll>
                <div className={isChartView ? 'p-20' : 'p-0'}>
                    {loading && <OptimizationPipelineChartEmptyState>Loading optimizer history...</OptimizationPipelineChartEmptyState>}
                    {!loading && error && <OptimizationPipelineChartEmptyState>Failed to load optimizer history.</OptimizationPipelineChartEmptyState>}
                    {!loading && !error && !hasDisplayData && <OptimizationPipelineChartEmptyState>No data available.</OptimizationPipelineChartEmptyState>}

                    {!loading && !error && hasDisplayData && (
                        <>
                            {isChartView ? (
                                <div className='w-full' style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
                                    <OptimizationPipelineChartFrame ref={chartRef} height={CHART_HEIGHT} minHeight={CHART_HEIGHT} overflowVisible>
                                        <ComposedChart data={chartData} margin={{ top: 20, left: 35, right: 35, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray='3 3' vertical={false} />
                                            <XAxis dataKey='ts' tick={{ fontSize: 10 }} tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} />
                                            <YAxis yAxisId='gain' tick={{ fontSize: 10 }} tickMargin={10} tickCount={7} type='number' tickFormatter={(tick) => formatAxisTick(tick, suffix)} domain={yDomain} />
                                            <YAxis yAxisId='cum' tick={{ fontSize: 10 }} orientation='right' tickMargin={10} tickCount={7} type='number' tickFormatter={(tick) => formatAxisTick(tick, cumSuffix)} domain={cumDomain} />
                                            <ReTooltip {...tooltipProps} />
                                            <ReferenceLine yAxisId='gain' y={0} stroke='#9CA3AF' strokeDasharray='4 4' />

                                            <Bar yAxisId='gain' dataKey='value' name='Value' radius={[4, 4, 0, 0]} background={renderEstimatedBackground}>
                                                {chartData.map((row, index) => {
                                                    let color;
                                                    let opacity = 1;

                                                    if (row.status === 'gap' || row.status === 'outlier' || row.status === 'skipped') {
                                                        color = 'transparent';
                                                        opacity = 0;
                                                    } else if (row.status === 'estimated') color = ESTIMATED_COLOR;
                                                    else if (row.status === 'warning') color = WARN_COLOR;
                                                    else color = (row.value ?? 0) >= 0 ? POS_COLOR : NEG_COLOR;

                                                    return <Cell key={row.ts || index} fill={color} fillOpacity={opacity} />;
                                                })}
                                            </Bar>

                                            <Line yAxisId='cum' type='monotone' dataKey='cumValue' stroke='#FFFFFF' strokeWidth={6} dot={false} name='Cumulative gain outline' />
                                            <Line yAxisId='cum' type='monotone' dataKey='cumValue' stroke={CUM_LINE_COLOR} strokeWidth={3} dot={false} name='Cumulative gain' />
                                        </ComposedChart>
                                    </OptimizationPipelineChartFrame>
                                </div>
                            ) : (
                                <OptimizationPipelinePopupTable tableConfig={tableConfig} rows={displayDaily} minHeight={400} rowKey={(row, index) => row?.ts || index} />
                            )}

                            {hasScale && useScaled && <div className='pt-12 text-center text-10 opacity-70'>Values shown in {unitOptimizerData?.uom} (multiplier {multiplier}).</div>}

                            <div className='mt-12 text-10 flex flex-wrap gap-16 justify-center items-center'>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: POS_COLOR }} /><span>+ Gain</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: NEG_COLOR }} /><span>- Gain</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: WARN_COLOR }} /><span>Result with inaccurate model</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: ESTIMATED_BG_COLOR }} /><span>No optimizer result</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: OUTLIER_BG_COLOR }} /><span>Outlier</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-12 rounded-sm' style={{ backgroundColor: SKIPPED_BG_COLOR }} /><span>Skipped</span></div>
                                <div className='flex items-center gap-4'><span className='inline-block w-12 h-2 rounded-sm' style={{ backgroundColor: CUM_LINE_COLOR }} /><span>Cumulative gain</span></div>
                            </div>
                        </>
                    )}
                </div>
            </PopupBody>
        </OptimizationPipelinePopup>
    );
}